// apps/client/src/engine/shader/worldPatch.ts — port of look-demo assets.ts patch(): wind and the curved world, plus (light and look,
// 2026-10-16) the occlusion fade of whole objects (engine/shader/fade.ts; it replaced the dithered see-through circle and near fade) and
// the ground map on the grass and sand (world/terrain/ground.ts).
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';
import { FADE_CLEAR_GLSL, FADE_GLSL, FADE_VIEWERS } from './fade';

export { FADE_VIEWERS };
const GR = tuning.light.ground, FD = tuning.light.fade;
/** A 1 × 1 ground map that changes nothing (no shade, path or lamp; a neutral patch) until a town paints its own. */
export const neutralGround: THREE.Texture = (() => {
  const t = new THREE.DataTexture(new Uint8Array([0, 0, 128, 0]), 1, 1, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
})();
/** uViewers: the chest (world) of each resident the fade follows, w = 1 when the slot is on, the first uViewerCount slots; uFadeCam: the
 *  camera (world); uFadeShape and uFadeNear: the fade's tuning (engine/shader/fade.ts); uFadeAlpha: a fully faded object's opacity;
 *  uFadeEdge: how much firmer its edges; uFadeClear = (radius, soft, chest, resident height) and uFadeClearK = (alpha, boost): the window a
 *  ghost clears over a resident it fades for (engine/shader/fade.ts FADE_CLEAR_GLSL).
 *  uGround*: the ground map and how the grass reads it: uGroundXf maps world (x, z) to its uv; path, shade and patch colours are
 *  linear; uGroundShadeK scales the shade (lower where shadow maps draw); uGroundLamp is the lamp pools' colour × intensity now.
 *  uGroundSun: the sun's soft shadows painted on the ground for low quality (its red channel), uGroundSunK their strength now (0 where
 *  shadow maps draw); uGroundSunOn is set by the renderer (1 on low) and read on the CPU only. */
export const worldUniforms: { uCurve: { value: number }; uCamXZ: { value: THREE.Vector2 }; uTime: { value: number };
  uViewers: { value: THREE.Vector4[] }; uViewerCount: { value: number }; uFadeCam: { value: THREE.Vector3 }; uFadeShape: { value: THREE.Vector4 };
  uFadeNear: { value: THREE.Vector2 }; uFadeAlpha: { value: number }; uFadeEdge: { value: number };
  uFadeClear: { value: THREE.Vector4 }; uFadeClearK: { value: THREE.Vector2 };
  uGround: { value: THREE.Texture }; uGroundXf: { value: THREE.Vector4 }; uGroundPath: { value: THREE.Color };
  uGroundShade: { value: THREE.Color }; uGroundShadeK: { value: number }; uGroundDry: { value: THREE.Color }; uGroundLush: { value: THREE.Color };
  uGroundLamp: { value: THREE.Color }; uGroundSun: { value: THREE.Texture }; uGroundSunShade: { value: THREE.Color }; uGroundSunK: { value: number };
  uGroundSunOn: { value: number } } = {
  uCurve: { value: tuning.camera.curve }, uCamXZ: { value: new THREE.Vector2() }, uTime: { value: 0 },
  uViewers: { value: Array.from({ length: FADE_VIEWERS }, () => new THREE.Vector4(0, 0, 0, 0)) }, uViewerCount: { value: 0 },
  uFadeCam: { value: new THREE.Vector3() },
  uFadeShape: { value: new THREE.Vector4(FD.soft, FD.front[0], FD.front[1], FD.strip) }, uFadeNear: { value: new THREE.Vector2(FD.near[0], FD.near[1]) },
  uFadeAlpha: { value: FD.alpha }, uFadeEdge: { value: FD.edge },
  uFadeClear: { value: new THREE.Vector4(FD.clear.radius, FD.clear.soft, FD.chest, tuning.move.residentHeight) }, uFadeClearK: { value: new THREE.Vector2(FD.clear.alpha, FD.clear.boost) },
  uGround: { value: neutralGround }, uGroundXf: { value: new THREE.Vector4(0, 0, 1, 1) }, uGroundPath: { value: new THREE.Color(GR.path.color) },
  uGroundShade: { value: new THREE.Color(GR.shade.color) }, uGroundShadeK: { value: 1 }, uGroundDry: { value: new THREE.Color(GR.patch.dry) },
  uGroundLush: { value: new THREE.Color(GR.patch.lush) }, uGroundLamp: { value: new THREE.Color(0, 0, 0) },
  uGroundSun: { value: neutralGround }, uGroundSunShade: { value: new THREE.Color(GR.sun.color) }, uGroundSunK: { value: 0 }, uGroundSunOn: { value: 0 },
};
/** The camera the fade measures from (world). The town sets it every frame; the title never does (nothing fades there). */
export function setFadeCamera(p: THREE.Vector3): void { worldUniforms.uFadeCam.value.copy(p); }
/** The residents the fade follows (you first, then the nearest others), by their unbent feet (world): the first FADE_VIEWERS, packed,
 *  the rest of the slots off. */
export function setViewers(feet: readonly THREE.Vector3[]): void {
  const n = Math.min(FADE_VIEWERS, feet.length);
  worldUniforms.uViewers.value.forEach((v, i) => { const f = feet[i]; if (i < n && f) v.set(f.x, f.y + FD.chest, f.z, 1); else v.set(0, 0, 0, 0); });
  worldUniforms.uViewerCount.value = n;
}

const uHorizon: { value: number } = { value: tuning.camera.horizon };
// aObj: the object this vertex belongs to, (x, z, −radius, top); see engine/shader/fade.ts. vCwFade: its fade (0…1).
const VERTEX_HEAD = 'uniform float uTime; uniform vec3 uWind; uniform float uCurve; uniform vec2 uCamXZ; uniform float uHorizon; varying vec2 vCwXZ;\n'
  + 'attribute vec4 aObj; varying float vCwFade;\n#ifdef CW_GHOST\nvarying vec3 vCwGhostPos;\n#endif\n' + FADE_GLSL;
const WIND = `#include <begin_vertex>
  float windK = uWind.x * smoothstep(uWind.y, uWind.z, position.y);
  transformed.x += sin(uTime * 1.7 + position.x * 0.45 + position.z * 0.25) * windK;
  transformed.z += cos(uTime * 1.3 + position.z * 0.5 + position.x * 0.2) * windK * 0.7;`;
/** Bends geometry down with distance from the camera target ("rolling log"); instances beyond the horizon are clipped (contract change
 *  13). Then the occlusion fade: a fading object leaves the main pass (and GTAO's prepass) whole, to be drawn by its translucent ghost
 *  (CW_GHOST: only fading objects); shadow casters (DEPTH_PACKING) keep casting either way. */
const CURVE = `#include <project_vertex>
  vec4 cwWorld = modelMatrix * vec4(transformed, 1.0);
  vec2 cwOrigin = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz;
  #ifdef USE_INSTANCING
    cwWorld = modelMatrix * instanceMatrix * vec4(transformed, 1.0);
    cwOrigin = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz;
  #endif
  vec2 cwD = cwWorld.xz - uCamXZ;
  cwWorld.y -= dot(cwD, cwD) * uCurve;
  mvPosition = viewMatrix * cwWorld;
  gl_Position = projectionMatrix * mvPosition;
  vCwXZ = cwWorld.xz;
  #ifdef CW_GHOST
    vCwGhostPos = cwWorld.xyz;
  #endif
  if (length(cwOrigin - uCamXZ) > uHorizon) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  vCwFade = 0.0;
  #ifndef DEPTH_PACKING
    vCwFade = cwFadeOf( aObj );
    #ifdef CW_GHOST
      if ( vCwFade <= 0.0 ) gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 );
    #else
      if ( vCwFade > 0.0 ) gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 );
    #endif
  #endif`;
// Shadow receivers look up the shadow map at the bent position too, matching the bent casters (customDepthMaterial below).
const WORLDPOS = `#include <worldpos_vertex>
  #if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0
    worldPosition = cwWorld;
  #endif`;
// A ghost's colour pass: the object at its fade's opacity (its depth pass, drawn just before, keeps only its nearest surface). Where the
// material is lit, the faces toward the camera clear the most and the edges stay firmer (uFadeEdge), so a ghosted tree keeps its shape;
// where it lies over a resident it fades for, it drops the firmer edges and clears further (cwClearOf), so it never veils them.
const GHOST_HEAD = FADE_CLEAR_GLSL + 'varying float vCwFade;\n';
const GHOST_ALPHA_LIT = `#include <opaque_fragment>
  float cwEdge = 1.0 - saturate( dot( normal, normalize( vViewPosition ) ) );
  gl_FragColor.a *= cwGhostCoverage( cwEdge, vCwFade, cwClearOf( vCwGhostPos ) );`;
const GHOST_ALPHA = `#include <opaque_fragment>
  gl_FragColor.a *= cwGhostCoverage( 0.0, vCwFade, cwClearOf( vCwGhostPos ) );`;

// The ground (materials with userData.ground: terrace tops and ramps true; the beach and the plaza's and paths' stones 'bare'): worn
// earth and then the meadow patches lean the grass's albedo (never a bare one's; the earth first, so a dry patch tints it too and it never
// reads lighter than the grass round it), soft shade and the low-quality sun shadows darken them all, after the map and vertex colours;
// the lamp pools add warm light after the emissive maps.
const GROUND_HEAD = `#define CW_GROUND
uniform sampler2D uGround; uniform vec4 uGroundXf; uniform vec3 uGroundPath; uniform vec3 uGroundShade; uniform float uGroundShadeK;
uniform vec3 uGroundDry; uniform vec3 uGroundLush; uniform vec3 uGroundLamp; varying vec2 vCwXZ;
uniform sampler2D uGroundSun; uniform vec3 uGroundSunShade; uniform float uGroundSunK;
`;
const GROUND_ALBEDO = `#include <color_fragment>
  vec2 cwGroundUv = ( vCwXZ - uGroundXf.xy ) * uGroundXf.zw;
  vec4 cwGround = texture2D( uGround, cwGroundUv );
  float cwPatch = cwGround.b * 2.0 - 1.0;
  #ifndef CW_BARE
    diffuseColor.rgb = mix( diffuseColor.rgb, uGroundPath, cwGround.g );
    diffuseColor.rgb *= mix( vec3( 1.0 ), cwPatch > 0.0 ? uGroundDry : uGroundLush, abs( cwPatch ) );
  #endif
  diffuseColor.rgb *= mix( vec3( 1.0 ), uGroundShade, cwGround.r * uGroundShadeK );
  diffuseColor.rgb *= mix( vec3( 1.0 ), uGroundSunShade, texture2D( uGroundSun, cwGroundUv ).r * uGroundSunK );`;
// The pool's warmth on whatever it lights: the lamp's own colour, as bright as the ground under it is light (grass stays amber, not lime).
const GROUND_LAMP = `#include <emissivemap_fragment>
  totalEmissiveRadiance += uGroundLamp * cwGround.a * ( dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) ) + 0.03 );`;

// "Patched" means the hook is installed. Not userData.patched alone: Material.clone() copies userData but not onBeforeCompile, so a
// clone of a patched material (wind clones and tints of the one KayKit atlas material) carries the flag without the patch.
const isPatched = (mat: THREE.Material): boolean => mat.onBeforeCompile === inject;
/** Whether `mat` carries the world patch (tests; the icon renderer must never draw with it). */
export const isWorldPatched = isPatched;
/** The program variant: wind clones, ground materials and ghosts compile their own (a ghost's depth and colour passes share one). */
const keyOf = (mat: THREE.Material) =>
  `world${mat.userData.wind ? '-wind' : ''}${mat.userData.ground ? `-ground${mat.userData.ground === 'bare' ? '-bare' : ''}` : ''}${mat.userData.ghost ? '-ghost' : ''}`;
function install(mat: THREE.Material): void {
  const key = keyOf(mat);
  mat.onBeforeCompile = inject; mat.customProgramCacheKey = () => key; mat.userData.patched = true; mat.needsUpdate = true;
}
/** Additive export: patch one material in place (used for GTAOPass's normal/depth override material). */
export function patchMaterial(mat: THREE.Material): void {
  if (!isPatched(mat)) install(mat);
}
function inject(this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms): void {
  const ghost = !!this.userData.ghost;
  Object.assign(shader.uniforms, worldUniforms, { uHorizon, uWind: { value: (this.userData.wind as THREE.Vector3 | undefined) ?? new THREE.Vector3() } });
  shader.vertexShader = (ghost ? '#define CW_GHOST\n' : '') + VERTEX_HEAD + shader.vertexShader.replace('#include <begin_vertex>', WIND)
    .replace('#include <project_vertex>', CURVE).replace('#include <worldpos_vertex>', WORLDPOS);
  if (ghost) {
    const lit = shader.fragmentShader.includes('#include <normal_fragment_begin>') && shader.fragmentShader.includes('vViewPosition');
    shader.fragmentShader = GHOST_HEAD + shader.fragmentShader.replace('#include <opaque_fragment>', lit ? GHOST_ALPHA_LIT : GHOST_ALPHA);
  }
  if (this.userData.ground && shader.fragmentShader.includes('#include <emissivemap_fragment>')) {
    shader.fragmentShader = (this.userData.ground === 'bare' ? '#define CW_BARE\n' : '') + GROUND_HEAD
      + shader.fragmentShader.replace('#include <color_fragment>', GROUND_ALBEDO)
      .replace('#include <emissivemap_fragment>', GROUND_LAMP);
  }
}
const depthCache = new WeakMap<THREE.Material, THREE.MeshDepthMaterial>();
export function patchObject(root: THREE.Object3D, opts: { wind?: readonly [number, number, number] } = {}): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.userData.worldPatched) return;
    m.userData.worldPatched = true;
    const fix = (src: THREE.Material) => {
      const mat = opts.wind ? src.clone() : src;
      if (opts.wind) mat.userData.wind = new THREE.Vector3(...opts.wind);
      if (!isPatched(mat)) install(mat);
      return mat;
    };
    m.material = Array.isArray(m.material) ? m.material.map(fix) : fix(m.material);
    const first = Array.isArray(m.material) ? m.material[0]! : m.material;
    let depth = depthCache.get(first);
    if (!depth) {                                       // shadows bend with the world too
      depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
      depth.userData.wind = first.userData.wind;
      depth.onBeforeCompile = inject;
      depthCache.set(first, depth);
    }
    m.customDepthMaterial = depth;
  });
}

/** A faded object's ghost, drawn after everything opaque: `depth` lays down only its nearest surface, then `color` draws that surface
 *  at the fade's opacity, so a whole tree or house turns clean and translucent, never showing its own insides. */
export interface GhostMaterials { depth: THREE.Material; color: THREE.Material }
const ghostCache = new WeakMap<THREE.Material, GhostMaterials>();
/** The ghost pair of a patched world material (cached: one pair per source material). */
export function ghostMaterials(src: THREE.Material): GhostMaterials {
  let g = ghostCache.get(src);
  if (!g) {
    const make = (mode: 'depth' | 'color') => {
      const m = src.clone();
      m.userData.wind = src.userData.wind;              // clone() turns userData into plain JSON
      m.userData.ghost = mode;
      m.transparent = true;
      m.depthWrite = mode === 'depth';
      m.colorWrite = mode === 'color';
      // Lit windows: the clock drives the shared pane material's glow every frame, and the ghost's follows it.
      if ((src as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
        Object.defineProperty(m, 'emissiveIntensity', { get: () => (src as THREE.MeshStandardMaterial).emissiveIntensity, set: () => {} });
      }
      install(m);
      return m;
    };
    g = { depth: make('depth'), color: make('color') };
    ghostCache.set(src, g);
  }
  return g;
}
